import type { AppConfig } from '@dte/otw-azure-functions/src/common/interfaces/containers/config/AppConfig';
import type { ModeDetail } from '@dte/otw-azure-functions/src/common/interfaces/containers/config/ModeDetail';
import type { StormSettings } from '@dte/otw-azure-functions/src/common/interfaces/containers/config/Settings';
import {
	DateFormatType,
	formatEstimateDate,
} from '@dte/otw-azure-functions/src/common/jobs/data/estimates/windows/formatEstimateDate';
import { formatEST } from '@dte/otw-utils-dates/src/format/formatEST';
import { safeParseDate } from '@dte/otw-utils-dates/src/parse/date';
import { Grid } from '@mantine/core';
import { type ReactNode, useState } from 'react';
import { AuthenticatedJsonContent } from '../../app/(client)/(authenticated)/content/jsonGet/AuthenticatedJsonContent';
import { getAppConfigURL } from '../../hooks/api/getAppConfigURL';
import { BorderedBox } from '../counts/BorderedBox';

export function getModeDetailsForAppConfig(
	appConfig: AppConfig | undefined,
	mode: string | undefined,
): ModeDetail | undefined {
	if (!mode) {
		return undefined;
	}

	return appConfig?.modesLookup?.[mode];
}

function globalEstimateBlock(appConfig: AppConfig | undefined, settings: StormSettings | undefined): ReactNode {
	const mode = settings?.mode;
	const modeDetails = getModeDetailsForAppConfig(appConfig, mode);

	if (!modeDetails?.globalEstimateRequired) {
		return undefined;
	}

	const date = safeParseDate(settings?.globalEstimate);
	const formattedDate = formatEstimateDate(date, DateFormatType.Long);

	return (
		<Grid>
			<Grid.Col span={3} style={{ whiteSpace: 'nowrap' }}>
				<b>Global Estimate</b>
			</Grid.Col>
			<Grid.Col span={'auto'}>
				{settings?.globalEstimatePercent} restored by {formattedDate}
			</Grid.Col>
		</Grid>
	);
}

interface Props {
	legend?: string;
	settings?: StormSettings;
	children?: ReactNode;
}

export function ModeDetails(props: Props): ReactNode {
	const appConfigURL = getAppConfigURL();
	const [appConfig, setAppConfig] = useState<AppConfig>();

	// Display the details of the mode in settings or the current one
	const settings = props.settings || appConfig?.settings;

	const mode = settings?.mode;
	const modeDetails = getModeDetailsForAppConfig(appConfig, mode);

	let content: ReactNode = null;
	if (modeDetails) {
		let stormModeMessage = 'False';
		if (modeDetails?.stormMode) {
			const activationDate = appConfig?.settings?.stormActivationDate;
			if (activationDate) {
				stormModeMessage = `Since ${formatEST(safeParseDate(activationDate), "EEEE, MMM d 'at' h:mm a")}`;
			} else {
				stormModeMessage = 'True';
			}
		}

		content = (
			<div>
				<Grid>
					<Grid.Col span={3}>
						<b>Mode</b>
					</Grid.Col>
					<Grid.Col span={'auto'}>{modeDetails.description}</Grid.Col>
				</Grid>
				<Grid>
					<Grid.Col span={3} style={{ whiteSpace: 'nowrap' }}>
						<b>Storm Mode</b>
					</Grid.Col>
					<Grid.Col span={'auto'}>{stormModeMessage}</Grid.Col>
				</Grid>
				{globalEstimateBlock(appConfig, settings)}
			</div>
		);
	}

	return (
		<BorderedBox legend={props.legend}>
			<AuthenticatedJsonContent url={appConfigURL} setData={setAppConfig}>
				{content}
			</AuthenticatedJsonContent>
		</BorderedBox>
	);
}
